import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import { Banners } from 'src/domains/layouts/webview/components/banners/Banners';
import { ConfigComponents } from '../../config/features/config';
import { useDevice } from 'src/domains/common/hooks/useDevice';
import { useCommon } from 'src/domains/common/Common';
import { EnvironmentState } from '../../state/environmentState/EnvironmentState';
import { useCookiebot } from 'src/domains/common/hooks/useCookiebot';
import { DeviceType } from 'src/domains/common/hooks/useDevice';
import { BannersContainer } from '../../webview/components/BannerBox/BannersContainer';
import { MarketingNotificationsState } from 'src/domains/players/state/MarketingNotificationsState';
import { DownloadAppBanner } from 'src/domains/layouts/webview/components/downloadAppBanner/DownloadAppBanner';
import { MarketingNotificationsBanner } from 'src/domains/layouts/webview/components/marketingNotificationsBanner/MarketingNotificationsBanner';
import { CookiesPolicyBox } from 'src/domains/layouts/webview/components/cookiesPolicyBox/CookiesPolicyBox';
import { ClosureAnnouncementTopBar } from 'src/domains/layouts/webview/components/closureAnnouncementTopBar/ClosureAnnouncementTopBar';

const getDownloadAppLink = (device: DeviceType | null): string | null => {
    const common = useCommon();
    const config = ConfigComponents.get(common).config;
    const { appDownloadLink } = config;

    if (device === 'Android' && appDownloadLink.googlePlayLink !== null) {
        const prefix = 'market';
        return appDownloadLink.googlePlayLink
            .replace('play.google.com/store/apps/', '')
            .replace('https', prefix)
            .replace('http', prefix);
    }

    if (device === 'iOS' && appDownloadLink.appStoreLink !== null) {
        const prefix = 'itms-apps';
        return appDownloadLink.appStoreLink.replace('https', prefix).replace('http', prefix);
    }

    return null;
};

const DownloadAppBannerRenderer = observer('DownloadAppBannerRenderer', (): JSX.Element | null => {
    const common = useCommon();
    const env = EnvironmentState.get(common);
    const isMobileApp = env.isMobileApp();
    const config = ConfigComponents.get(common).config;
    const { appDownloadLink } = config;
    const cookiebot = useCookiebot();
    const device = useDevice();
    const link = getDownloadAppLink(device);

    if (isMobileApp === true || appDownloadLink.showBanner === false || link === null) {
        return null;
    }

    return (
        <DownloadAppBanner
            link={link}
            cookiebot={cookiebot}
        />
    );
});

const MarketingNotificationsBannerRenderer = observer(
    'MarketingNotificationsBannerRenderer',
    (): JSX.Element | null => {
        const common = useCommon();
        const { isBannerAvailable } = MarketingNotificationsState.get(common);
        if (isBannerAvailable) {
            return <MarketingNotificationsBanner key='marketing-banner' />;
        }
        return null;
    }
);

const CookieBoxRenderer = observer('CookieBoxRenderer', (): JSX.Element | null => {
    const common = useCommon();
    const env = EnvironmentState.get(common);
    const isMobileApp = env.isMobileApp();
    if (isMobileApp === false && env.isBrowser) {
        return <CookiesPolicyBox key='cookie-banner' />;
    }

    return null;
});

const ClosureAnnouncementRenderer = observer('ClosureAnnouncementRenderer', (): JSX.Element | null => {
    const common = useCommon();
    const config = ConfigComponents.get(common).config;
    if (config.midnightMigrationScreen === true) {
        return <ClosureAnnouncementTopBar key='closure-announcement-banner' />;
    }

    return null;
});

export const BannersMain = observer('BannersMain', () => {
    return (
        <Banners>
            <DownloadAppBannerRenderer />
            <MarketingNotificationsBannerRenderer />
            <CookieBoxRenderer />

            <ClosureAnnouncementRenderer />
            <BannersContainer />
        </Banners>
    );
});
